import { createContext, forwardRef, useContext } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * Tabs — portal tabs on @radix-ui/react-tabs.
 *
 *   <Tabs defaultValue="all">
 *     <TabsList variant="line">
 *       <TabsTrigger value="all">All</TabsTrigger>
 *       <TabsTrigger value="open">Open</TabsTrigger>
 *     </TabsList>
 *     <TabsContent value="all">…</TabsContent>
 *     <TabsContent value="open">…</TabsContent>
 *   </Tabs>
 *
 * TabsList comes in two flavours and TabsTrigger picks its styling up
 * from the list it sits in (via context, like Select's placeholder):
 * - `line` — underline nav; the active trigger carries a border-focused
 *   underline that overlaps the list's own border
 * - `boxed` — segmented toolbar; triggers sit in a raised well and the
 *   active trigger lifts out on surface-default
 */

type TabsListVariant = NonNullable<TabsListProps["variant"]>;

const TabsListContext = createContext<TabsListVariant | undefined>(undefined);

const tabsListVariants = cva("inline-flex items-center", {
  variants: {
    variant: {
      line: "gap-1 border-b border-border-default",
      boxed: "gap-0.5 rounded-lg border border-border-default bg-surface-raised p-0.5",
    },
  },
  defaultVariants: {
    variant: "line",
  },
});

const tabsTriggerVariants = cva(
  "inline-flex select-none items-center justify-center whitespace-nowrap type-body-md font-medium outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-border-focused focus-visible:ring-offset-1 focus-visible:ring-offset-surface-canvas disabled:pointer-events-none disabled:opacity-38",
  {
    variants: {
      variant: {
        line: "-mb-px h-9 border-b-2 border-transparent px-3 text-content-secondary hover:text-content-primary data-[state=active]:border-border-focused data-[state=active]:text-content-primary",
        boxed:
          "h-7 rounded-md px-3 text-content-secondary hover:text-content-primary data-[state=active]:bg-surface-default data-[state=active]:text-content-primary data-[state=active]:shadow-card",
      },
    },
    defaultVariants: {
      variant: "line",
    },
  },
);

export interface TabsProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {}

/** Controlled/uncontrolled root. Renders no DOM element. */
export function Tabs(props: TabsProps) {
  return <TabsPrimitive.Root {...props} />;
}
Tabs.displayName = "Tabs";

export interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

export const TabsList = forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant, ...props }, ref) => {
  return (
    <TabsListContext.Provider value={variant ?? "line"}>
      <TabsPrimitive.List
        ref={ref}
        className={cn(tabsListVariants({ variant }), className)}
        {...props}
      />
    </TabsListContext.Provider>
  );
});
TabsList.displayName = "TabsList";

export interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

/** Variant defaults to the owning TabsList's — only pass it to override. */
export const TabsTrigger = forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant, ...props }, ref) => {
  const listVariant = useContext(TabsListContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        tabsTriggerVariants({ variant: variant ?? listVariant }),
        className,
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = "TabsTrigger";

export interface TabsContentProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> {}

export const TabsContent = forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  TabsContentProps
>(({ className, ...props }, ref) => {
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn("outline-none", className)}
      {...props}
    />
  );
});
TabsContent.displayName = "TabsContent";
